/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { NgTemplateOutlet } from '@angular/common';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { afterNextRender, Component, inject, Injector, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AbstractObject, uuid } from '@src/app/model/abstract-object';
import { Action } from '@src/app/model/action';
import { ActionTemplate } from '@src/app/model/ActionTemplate';
import { Attachment } from '@src/app/model/attachment';
import { Omc } from '@src/app/model/Omc';
import { RequestTemplate } from '@src/app/model/RequestTemplate';
import { BCode } from '@src/app/model/bcode';
import { CommsTemplateType } from '@src/app/model/CommsTemplate';
import { Schedule } from '@src/app/model/schedule';
import { RequestBudget, ServiceRequest } from '@src/app/model/serviceRequest';
import { Supplier } from '@src/app/model/supplier';
import { Unit } from '@src/app/model/unit';
import { Person } from '@src/app/model/person';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { RequestContext } from '@src/app/modules/crm/request-context.service';
import { RequestTemplatesService } from '@src/app/modules/crm/request-templates.service';
import { CreatedRequest, RequestService } from '@src/app/modules/crm/request.service';
import { SupplierService } from '@src/app/modules/supply/supplier.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { DateHelper } from '@src/app/shared/dateHelper';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { GridField } from '@src/app/shared/grid/grid-field';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { of } from 'rxjs';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { maxValue, minValue, notBeforeToday, required } from '@src/app/shared/validators';

/** One row of the attachments step - a file on the parent, and what this request does with it. */
interface RequestFile {
    attachment: Attachment;
    share: boolean;
    includeInMail: boolean;
}

/**
 * Which kind of request is being raised. A work order has a supplier, and with it the work step and
 * the mail that carries it; a case has neither. Matches ServiceRequest.requestTypes, but the server
 * derives requestType from the supplier rather than being told - so this only decides which steps
 * are drawn and what is posted.
 */
export type NewRequestType = 'case' | 'workOrder';

/**
 * Either a parent request, whose team, files and wording the new request starts from, or - raised
 * from the new menu with nothing to hang off - just the tenancy it belongs to. One or the other.
 *
 * unit and person seed the People step when the request is raised from a record that already knows
 * them, such as the unit page. The parent wins where there is one.
 */
export interface NewRequestStepperData {
    requestType: NewRequestType;
    parent?: ServiceRequest;
    context?: RequestContext;
    unit?: Unit;
    person?: Person;
    /** Raise from a standard request: adds the first step, with the template still to be chosen. */
    templated?: boolean;
    /** A template already settled on - the step shows it, but it is not the user's to change. */
    templateId?: uuid;
    /** The recurring job this occurrence belongs to. Carried to the server, never shown. */
    jobId?: uuid;
    plannedDate?: string;
    /** Presetting a supplier makes this a work order, whatever requestType says. */
    supplierId?: uuid;
    /** Raised from a ballot: linked to it on save, and sourced as a meeting. */
    ballotId?: uuid;
    forumId?: uuid;
}

/** What the last step's button does. The options offered depend on the supplier having an address. */
const CREATE_ONLY = 'create';
const CREATE_AND_SEND = 'send';

/**
 * Raise a request: name the work, confirm who it concerns, plan the follow ups, and - for a work
 * order - pick the supplier and budget, choose which of the parent's files travel with it and write
 * the mail that carries it.
 *
 * The pattern here is the one to copy for future steppers. A FieldSet per step owns that step's
 * fields and hands over a FormGroup, which is all <mat-step [stepControl]> needs to gate Next. The
 * step's layout is then drawn here rather than through <app-field-set>: `mat-form-field` is
 * width:100% globally, so a control is exactly as wide as the div it is dropped into, and a table
 * layout of percentage cells cannot give a date field the 190px it wants while the title beside it
 * takes the rest. Literal <mat-step>s leave room for the conditional Work Order and Mail steps and
 * for content no field covers (the file list). Nothing saves until the last step: one POST creates
 * the request, copies the files and sends the mail.
 */
@Component({
    selector: 'app-new-request-stepper',
    templateUrl: './new-request-stepper.component.html',
    styleUrl: './new-request-stepper.component.scss',
    imports: [NgTemplateOutlet, ReactiveFormsModule, MatStepperModule, MatButtonModule,
        MatButtonToggleModule, MatIconModule, MatTooltipModule, DialogBannerComponent,
        CtlHolderComponent],
})
export class NewRequestStepperComponent {
    dialogRef = inject<MatDialogRef<NewRequestStepperComponent>>(MatDialogRef);

    private data = inject<NewRequestStepperData>(MAT_DIALOG_DATA);
    /** Null when raised from the new menu - there is then no parent to copy from or link to. */
    parent: ServiceRequest = this.data.parent ?? null;
    /** A preset supplier is the work order, whichever type the caller asked for. */
    isWorkOrder = this.data.requestType === ServiceRequest.typeWorkOrder.id || !!this.data.supplierId;
    /** Built from a standard request: the template step is drawn, and the plan comes from it. */
    isTemplated = !!(this.data.templated || this.data.templateId);

    /**
     * A horizontal stepper header cannot show six steps on a phone, so the stepper turns vertical
     * and the dialog goes full bleed. Kept live rather than read once: a rotate changes the answer.
     */
    isPhone = inject(IsNarrowService).isPhone();

    /**
     * Follow ups are skipped on a phone. The grid is barely usable at that width, and an agent
     * logging something from the car park wants the request in, not a plan - the request simply
     * opens unplanned, the way one raised by email does. A templated request keeps them whatever
     * the width: the plan is the whole point of building from a standard request.
     *
     * Settled when the dialog opens rather than tracked live, so rotating the phone changes the
     * stepper's orientation without adding or removing a step under the user.
     */
    readonly hasFollowUps = !this.isPhone || this.isTemplated;
    /** Where the request belongs. Off the parent when there is one, otherwise resolved before we opened. */
    context: RequestContext = this.data.context ?? {
        omcId: this.data.parent?.omcId,
        teamId: this.data.parent?.teamId,
        agentTeamId: this.data.parent?.agentTeamId as string,
        omc: this.data.parent?.omc,
    };

    requestTypes = ServiceRequest.requestTypes;

    private agentDataSvc = inject(AgentDataService);
    private supplierSvc = inject(SupplierService);
    private currentUserSvc = inject(CurrentUserService);
    private reqSvc = inject(RequestService);
    private reqTemplateSvc = inject(RequestTemplatesService);
    private cds = inject(ConfirmDialogService);
    private msgSvc = inject(MessageService);
    private injector = inject(Injector);

    private bCodes: BCode[] = [];
    private schedules: Schedule[] = [];

    /** The picked supplier, kept for their email address - the field itself only holds the id. */
    supplier: Supplier = null;

    files: RequestFile[] = [];

    saving = false;

    /** The close action is added once, the first time the Follow Ups step is reached. */
    private closeSeeded = false;
    /** The template as last applied, with its actions in offset order. Null until one is chosen. */
    private template: RequestTemplate = null;
    private workOrderPrepared = false;

    @ViewChild('followUpStep') private followUpStep: MatStep;
    @ViewChild('stepper') private stepper: MatStepper;

    /*
     * The standard request this one is built from, and the date its plan hangs off.
     *
     * The template list is the agency's, whichever team the user is standing in, so it is fetched
     * with the plain service call - forcing the OMC team would find nothing. The list read does not
     * carry the template's actions, so choosing one fetches the whole record before it is applied.
     */
    templateField = FormPicklistComponent.make($localize`Standard Request`, 'templateId', 'template',
        {
            service: this.reqTemplateSvc, allowSelectNone: false,
            refreshes: [(picked: RequestTemplate) => this.templatePicked(picked)],
        },
        { validators: [required] }
    );

    /*
     * Only the anchor for the template's offsets - it is not the request's own planned date, which
     * is on its way out and is no longer posted.
     *
     * Optional: left blank the plan starts now, with the earliest action today and the rest at their
     * usual spacing after it (planAnchor). A date works the plan towards that day instead.
     */
    plannedDateField = FormDateComponent.make($localize`Planned Date`, 'plannedDate', {
        sendServer: false,
        validators: [notBeforeToday],
        valueChanges: () => this.replanActions(),
    });

    templateFS = new FieldSet({ fields: [this.templateField, this.plannedDateField] }, true);

    titleField = ServiceRequest.getTitleField();

    // No hint: it only repeated the step's own instruction, and bought a lone (i) icon floating
    // off to the right of the box for the privilege.
    descriptionField = ServiceRequest.getDescriptionField({ heightInFields: 4 });

    sourceField = ServiceRequest.getSourcePicklist();

    prioField = ServiceRequest.getPriorityField();

    /*
     * Classification has no field here, but a standard request carries the template's, and the
     * server takes it on creation - so it rides along rather than being lost between the two.
     */
    topicField = FieldMaker.idHolder('srTopic');
    classField = FieldMaker.idHolder('srClass');

    summaryFS = new FieldSet({
        fields: [this.titleField, this.sourceField, this.prioField, this.descriptionField,
            this.topicField, this.classField],
    }, true);

    /*
     * The same three the request page's People tile edits. A child request inherits them from its
     * parent exactly as a supplier's mailed reply does (InboundMail::createChildRequest), so the
     * step is a confirmation rather than a fresh set of decisions.
     */
    ownedByField = ServiceRequest.makeOwnedByField();
    unitContact = ServiceRequest.makeUnitAndContactFields();

    peopleFS = new FieldSet({
        fields: [this.ownedByField, this.unitContact.unitField, this.unitContact.personField,
            this.unitContact.contactLinkField],
    }, true);

    supplierField = ServiceRequest.makeSupplierField().override({ validators: [required] });

    budgetField = ServiceRequest.makeBudgetGridField(this.bCodes, this.schedules, () => this.context.teamId);

    /*
     * Always offered, so the choice sits squarely beside the supplier rather than appearing and
     * vanishing. Its options are rebuilt on each pick - a supplier with no address gets the one
     * option, which says why it is the only one.
     */
    mailModeField = FormPicklistComponent.make($localize`When Finished`, 'mailMode', null,
        { items: this.mailModeItems(), allowSelectNone: false },
        // sendServer: false - it drives the save rather than being part of it; buildRequest()
        // turns it into the mailToSupplier flag the endpoint actually wants.
        { sendServer: false, valueChanges: () => this.mailModeChanged() }
    );

    workFS = new FieldSet({ fields: [this.supplierField, this.mailModeField, this.budgetField] }, true);

    /*
     * The plan for chasing this request. Deliberately four columns - what, when, how long, who -
     * because the prompt to write a plan matters more here than anything the full action editor
     * offers. A past planned date is refused outright: there is no sense in raising a reminder to
     * do something yesterday, so a template whose offsets reach back before today has to be
     * rescheduled here rather than being quietly pulled forward.
     */
    followUpField = new GridField({
        field: { label: 'Follow Ups', value: 'actions' },
        rowFactory: () => [
            // teamId is not a column - it is what the owner picker's forceTeamId reads off the row,
            // so the standard request owner field works here without being rebuilt for the grid.
            FieldMaker.idHolder('teamId'),
            // The action's link back to the template it came from. The template's own wording is
            // read through it when the action is opened, so none of it is copied onto the row.
            FieldMaker.idHolder('templateId'),
            // Not a column on actions - kept so a change of planned date can re-derive this row.
            FieldMaker.idHolder('offset', { sendServer: false }),
            FormTextComponent.make('Action', 'title', { validators: [required] }),
            FormDateComponent.make('Planned', 'plannedDate', { validators: [required, notBeforeToday] }),
            // Narrower than it asks for: in a grid it draws a units picker beside the number, and
            // the dialog is 760px - left to itself it takes the room the action's own name needs.
            FormDurationComponent.make('Duration', 'timeSpent',
                { validators: [minValue(0), maxValue(960)], cellOpts: { width: '8em' } }),
            ServiceRequest.makeOwnedByField().override({ validators: [required] }),
            FieldMaker.deleteGridRow(),
        ],
        objFactory: () => of(this.newFollowUp('')),
        newOptionText: $localize`Add a follow up`,
    });

    followUpFS = new FieldSet({ fields: [this.followUpField] }, true);

    // richTextHeight is the whole editor, toolbar included - sized to fill the step body
    mailTextField = FormRichTextComponent
        .make($localize`Mail Text`, 'mailText', { validators: [required], richTextHeight: '340' })
        .setCommsTemplate(CommsTemplateType.REQUEST);

    mailFS = new FieldSet({ fields: [this.mailTextField] }, true);

    constructor() {
        if (this.isWorkOrder) {
            this.prepareWorkOrder();
        }
        inject(IsNarrowService).detectVeryNarrow().pipe(takeUntilDestroyed())
            .subscribe(phone => this.isPhone = phone);

        // Fills the step body: 50px header + 40px add button (it scrolls with the rows) + the rest.
        // A phone has no room for that once the vertical stepper's own header rows are in.
        const gridHeight = this.isPhone ? 180 : 276;
        this.budgetField.fixedGridHeight = gridHeight;
        this.followUpField.fixedGridHeight = gridHeight;

        if (this.parent) {
            this.sourceField.picklist.items.unshift(ServiceRequest.srcResponsive);
        }

        // teamId travels on the seed because the supplier picker reads it off the record to know
        // which tenant to list suppliers from - an agent standing in their own team sees none without it.
        // Wording comes from the parent; raised from the new menu there is nothing to copy.
        const seed = new ServiceRequest({
            title: this.titleFor(this.isWorkOrder),
            description: this.parent?.description,
            requestSource: this.initialSource(),
            priorityId: this.parent?.priorityId ?? ServiceRequest.prioNormal.id,
            teamId: this.context.teamId,
            omcId: this.context.omcId,
            // The People step's pick-links render from these, not from the ids, so the objects travel too.
            ownedById: this.parent?.ownedById,
            ownedByName: this.parent?.ownedByName,
            unitId: this.parent?.unitId ?? this.data.unit?.id,
            unit: this.parent?.unit ?? this.data.unit,
            personId: this.parent?.personId ?? this.data.person?.id,
            person: this.parent?.person ?? this.data.person,
        });
        this.workFS.setValue(seed);
        this.summaryFS.setValue(seed);
        this.peopleFS.setValue(seed);
        ServiceRequest.offerContactLink(this.unitContact.contactLinkField, seed.person);
        if (!seed.ownedById) {
            this.defaultTheOwner();
        }
        this.mailModeField.control.setValue(CREATE_ONLY, { emitEvent: false });

        this.files = (this.parent?.attachments ?? []).map(
            attachment => ({ attachment, share: false, includeInMail: false })
        );

        if (this.data.plannedDate) {
            this.plannedDateField.control.setValue(this.data.plannedDate, { emitEvent: false });
        }
        if (this.data.templateId) {
            this.lockToTemplate(this.data.templateId);
        }
        if (this.data.supplierId) {
            this.presetSupplier(this.data.supplierId);
        }
    }

    /**
     * A template the caller settled on - a recurring job's, or the follow on of a request just
     * closed. It is shown so the user knows what they are raising, but it is not theirs to change.
     */
    private lockToTemplate(templateId: uuid) {
        this.templateField.control.setValue(templateId, { emitEvent: false });
        this.templateField.disableControl();
        this.loadTemplate(templateId);
    }

    /**
     * The supplier came with the request rather than being chosen on the Work Order step, so the
     * step still has to be told - it is what seeds the budget lines and settles whether there is
     * anywhere to send the order.
     */
    private presetSupplier(supplierId: uuid) {
        this.supplierSvc.getOne<Supplier>(supplierId, null, this.context.teamId).subscribe(supplier => {
            if (!supplier) {
                return;
            }
            this.supplierField.setPicked(supplier);
            this.supplierPicked(supplier);
        });
    }

    /**
     * A different standard request replaces the plan, so say so before throwing away follow ups the
     * user may have edited. Cancelling puts the picklist back on the template that is actually applied.
     */
    private templatePicked(picked: RequestTemplate) {
        if (!picked || picked.id === this.template?.id) {
            return;
        }
        if (this.followUpField.control.length === 0) {
            this.loadTemplate(picked.id);
            return;
        }
        this.cds.openChoice({
            title: $localize`Change standard request?`,
            msg: $localize`The follow ups on this request will be replaced by the plan from ${picked.name}.`,
            options: [
                { name: $localize`Replace`, action: () => this.loadTemplate(picked.id) },
                { name: $localize`Cancel`, action: () => this.revertTemplate() },
            ],
        }, false);
    }

    private revertTemplate() {
        this.templateField.control.setValue(this.template?.id ?? null, { emitEvent: false });
    }

    /** The list read carries no actions, so the whole record is fetched before it can be applied. */
    private loadTemplate(templateId: uuid) {
        this.reqTemplateSvc.getOne<RequestTemplate>(templateId, new RequestTemplate()).subscribe(template => {
            if (template) {
                this.applyTemplate(template);
            }
        });
    }

    /**
     * What the standard request settles: the wording, the classification, who it belongs to, and
     * the plan. Not the category - that is on its way out - and not a planned date on the request
     * itself, which is no longer posted; the date on this step only dates the actions.
     */
    private applyTemplate(template: RequestTemplate) {
        this.template = template;
        template.actions = [...(template.actions ?? [])].sort((a, b) => a.offset - b.offset);

        this.titleField.control.setValue(template.name);
        this.descriptionField.control.setValue(template.description);
        if (template.priorityId) {
            this.prioField.control.setValue(template.priorityId);
        }
        this.topicField.control.setValue(template.srTopic ?? null);
        this.classField.control.setValue(template.srClass ?? null);
        this.markRecurringSource();

        const owner = Omc.getRoleUser(this.context.omc, template.ownerOmcRole);
        if (owner?.id && owner.name) {
            this.ownedByField.setPicked(owner);
        }

        this.buildTemplatePlan();
    }

    private initialSource(): string {
        if (this.parent) {
            return ServiceRequest.srcResponsive.id;
        }
        return this.data.ballotId ? ServiceRequest.srcMeeting.id : null;
    }

    /**
     * A standard request was raised on a schedule, not logged by whoever is standing here, so the
     * source says so. 'recurring' is not in the subset an agent may choose, the same way
     * 'responsive' is not - and the items array is already a copy, so adding to it is safe.
     */
    private markRecurringSource() {
        const items = this.sourceField.picklist.items;
        if (!items.find(item => item.id === ServiceRequest.srcRecurring.id)) {
            items.unshift(ServiceRequest.srcRecurring);
        }
        this.sourceField.control.setValue(ServiceRequest.srcRecurring.id);
    }

    /**
     * The template's actions become the plan, in offset order. Nothing is clamped: where the user
     * gave a date, an action whose offset reaches back before today keeps that date and the grid
     * stays invalid until they move it. A template with no actions has no plan to give, so the
     * request opens with the one close action every other request opens with.
     */
    private buildTemplatePlan() {
        this.followUpField.deleteAll();
        if (!this.schedulesActions) {
            this.closeSeeded = false;
            this.seedCloseAction();
            return;
        }
        this.closeSeeded = true;
        for (const actionTemplate of this.templateActions()) {
            this.followUpField.control.addRow(this.templateFollowUp(actionTemplate), false, false);
        }
        this.followUpField.control.refresh();
    }

    private templateFollowUp(actionTemplate: ActionTemplate): Action {
        const action = this.newFollowUp(actionTemplate.name);
        action.templateId = actionTemplate.id;
        action.offset = actionTemplate.offset;
        action.timeSpent = actionTemplate.timeSpent;
        action.plannedDate = this.dateForOffset(actionTemplate.offset);

        const owner = Omc.getRoleUser(this.context.omc, actionTemplate.ownerOmcRole);
        if (owner?.id && owner.name) {
            action.ownedById = owner.id;
            action.ownedByName = owner.name;
        }
        return action;
    }

    private dateForOffset(offset: number): string {
        return DateHelper.addDays(this.planAnchor(), offset);
    }

    /**
     * The day the offsets are measured from. The user's date where they gave one; otherwise the day
     * that puts the earliest action on today, so an undated plan starts now rather than making the
     * user pick a date whose only job is to be the zero point.
     */
    private planAnchor(): string {
        return this.plannedDateField.control.value || DateHelper.today(-this.earliestOffset());
    }

    private earliestOffset(): number {
        const offsets = this.templateActions().map(action => action.offset ?? 0);
        return offsets.length > 0 ? Math.min(...offsets) : 0;
    }

    /** Moving the date moves the plan with it. A row the user added by hand has no offset, so it stays put. */
    private replanActions() {
        for (const row of this.followUpField.control.gridRows()) {
            const offset = row.controls.offset.value;
            if (offset === null || offset === undefined) {
                continue;
            }
            row.controls.plannedDate.setValue(this.dateForOffset(offset));
        }
    }

    private templateActions(): ActionTemplate[] {
        return this.template?.actions ?? [];
    }

    /** Whether there is anything for the planned date to date - it is not offered otherwise. */
    get schedulesActions(): boolean {
        return this.templateActions().length > 0;
    }

    /** What the date on this step actually means, in terms of the plan it is about to lay out. */
    get plannedHelp(): string {
        const actions = this.templateActions();
        if (actions.length === 0) {
            return '';
        }
        return this.plannedDateField.control.value ? this.datedHelp(actions) : this.undatedHelp(actions);
    }

    /** With a date the offsets read against it, which is what the template's own wording describes. */
    private datedHelp(actions: ActionTemplate[]): string {
        const first = actions[0];
        if (actions.length === 1) {
            return $localize`${first.name} will be planned ${this.offsetText(first.offset)}.`;
        }
        const last = actions[actions.length - 1];
        return $localize`${first.name} will be planned ${this.offsetText(first.offset)}, through to
            ${last.name} ${this.offsetText(last.offset)}.`;
    }

    /** No date, so the plan starts today and the offsets only say how far apart the actions fall. */
    private undatedHelp(actions: ActionTemplate[]): string {
        const first = actions[0];
        if (actions.length === 1) {
            return $localize`Leave the date blank and ${first.name} will be planned for today.`;
        }
        const last = actions[actions.length - 1];
        return $localize`Leave the date blank and ${first.name} will be planned for today, through to
            ${last.name} ${this.spanText(last.offset - first.offset)}.`;
    }

    private spanText(days: number): string {
        return days ? $localize`${days} days later` : $localize`on the same day`;
    }

    private offsetText(offset: number): string {
        if (!offset) {
            return $localize`on the day`;
        }
        return offset < 0
            ? $localize`${-offset} days before`
            : $localize`${offset} days after`;
    }

    /**
     * Show who will pick the request up rather than leaving it blank for the server to fill in -
     * nothing on the form tells the user what it would choose. Same rule the server applies to an
     * unowned request, so what is shown here is what would have been saved anyway.
     */
    private defaultTheOwner() {
        const owner = Omc.getDefaultRequestOwner(this.context.omc);
        // Without a name the link renders empty while still posting an owner - better left blank.
        if (owner?.id && owner.name) {
            this.ownedByField.setPicked(owner);
        }
    }

    /** A work order is the supplier's part of the parent; a case is another take on the same thing. */
    private titleFor(isWorkOrder: boolean): string {
        if (!this.parent) {
            return '';
        }
        return isWorkOrder ? $localize`Supplier: ${this.parent.title}` : this.parent.title;
    }

    setRequestType(type: NewRequestType) {
        const workOrder = type === ServiceRequest.typeWorkOrder.id;
        if (workOrder === this.isWorkOrder) {
            return;
        }
        const wasOn = this.stepKeys()[this.stepper.selectedIndex];
        this.isWorkOrder = workOrder;
        if (workOrder) {
            this.prepareWorkOrder();
        }
        this.retitle(!workOrder);
        // The step is only added or removed on the next round of change detection, so where that
        // leaves the user can only be settled once it has been. afterNextRender rather than a
        // timeout because the old index can be past the end of the shortened list in between, and
        // a stepper with nothing selected collapses to its header - a timeout would paint that.
        afterNextRender(() => this.landOn(wasOn), { injector: this.injector });
    }

    /**
     * The seeded title names the supplier's half of the parent, so it follows the switch - but only
     * while it is still the seed. Anything the user has written is theirs.
     */
    private retitle(wasWorkOrder: boolean) {
        if (this.titleField.control.value !== this.titleFor(wasWorkOrder)) {
            return;
        }
        this.titleField.control.setValue(this.titleFor(this.isWorkOrder));
    }

    /** The steps as the template draws them, in order - what the stepper's indexes actually mean. */
    private stepKeys(): string[] {
        return [
            ...(this.isTemplated ? ['template'] : []),
            'summary', 'people',
            ...(this.isWorkOrder ? ['work'] : []),
            ...(this.hasFollowUps ? ['followUp'] : []),
            ...(this.hasFiles ? ['attach'] : []),
            ...(this.mailToSupplier ? ['mail'] : []),
        ];
    }

    /**
     * Whether any step follows this one - which is what decides whether its button bar offers Next
     * or the single save that creates the request. Asked of stepKeys() rather than worked out in
     * the template, because which steps exist depends on the type, the files, the mail and the
     * width all at once.
     */
    hasStepAfter(key: string): boolean {
        const keys = this.stepKeys();
        return keys.indexOf(key) < keys.length - 1;
    }

    /**
     * Where the switch leaves the user. Becoming a work order there is now a supplier to pick, so
     * the new step is as far as they can be - though they stay put if they had not reached it.
     * Becoming a case, the step they were standing on may have gone with the type, and whatever
     * took its place is where they end up.
     */
    private landOn(wasOn: string) {
        const keys = this.stepKeys();
        const was = keys.indexOf(wasOn);
        const work = keys.indexOf('work');
        let index = was < 0 ? Math.min(this.stepper.selectedIndex, keys.length - 1) : was;
        if (work >= 0) {
            index = Math.min(index, work);
        }
        this.stepper.selectedIndex = index;
        // A landing that does not move the index emits no selection change, so seed from here too.
        if (this.stepper.selected === this.followUpStep) {
            this.seedCloseAction();
        }
    }

    private prepareWorkOrder() {
        if (this.workOrderPrepared) {
            return;
        }
        this.workOrderPrepared = true;
        this.loadOmcBudgetLists();
        this.supplierField.refreshes.push((s: Supplier) => this.supplierPicked(s));
    }

    private newFollowUp(title: string): Action {
        const action = new Action();
        action.title = title;
        action.teamId = this.context.teamId;
        action.ownedById = this.ownedByField.control?.value ?? this.currentUserSvc.currentUser?.id;
        // The picker renders from the flat name, not the id, so carry what the step already knows.
        action.ownedByName = this.ownedByField.displayText;
        return action;
    }

    /** Seeded on arrival rather than in the constructor, so it takes the owner the People step settled on. */
    stepChanged(event: StepperSelectionEvent) {
        if (event.selectedStep === this.followUpStep) {
            this.seedCloseAction();
        }
    }

    /**
     * Every request opens with one dated action to close it, so the user has to decide: give it a
     * date, or delete the row and accept an unplanned request. Leaving the choice to a planned date
     * on the request is what used to produce requests nobody was reminded about.
     */
    private seedCloseAction() {
        if (this.closeSeeded || !this.hasFollowUps) {
            return;
        }
        this.closeSeeded = true;
        this.followUpField.control.addRow(this.newFollowUp($localize`Close this request`), true, false);
    }

    /** What the switch on the button bar shows as selected. */
    get requestType(): NewRequestType {
        return this.isWorkOrder ? 'workOrder' : 'case';
    }

    /** What is being raised, for the banner, the finish button and the message that follows it. */
    get typeName(): string {
        return this.isWorkOrder ? $localize`Work Order` : $localize`Case`;
    }

    /**
     * The OMC is named in the banner, because picking it is the one decision made before the dialog
     * opens and the only place it can be checked afterwards.
     */
    get title(): string {
        const omcName = this.context.omc?.name;
        return omcName ? $localize`New ${this.typeName} - ${omcName}` : $localize`New ${this.typeName}`;
    }

    /** The date is only mentioned once there is a plan for it to move, and then only as an option. */
    get templateHelp(): string {
        if (!this.schedulesActions) {
            return $localize`Choose the standard request to build from.`;
        }
        return $localize`Choose the standard request to build from, and the date its plan should work
            towards - or leave the date blank to start the plan today.`;
    }

    get summaryHelp(): string {
        return this.isWorkOrder
            ? $localize`Enter the basic details of the work order, with a full description of the work required.`
            : $localize`Enter the basic details of the case, with a full description of what needs to be done.`;
    }

    /** Three readings: nothing to inherit, inherited for a supplier to see, inherited for the office. */
    get peopleHelp(): string {
        if (!this.parent) {
            return $localize`Confirm who owns this request, and which unit and contact it concerns.`;
        }
        if (this.isWorkOrder) {
            return $localize`Confirm who owns this work order, and which unit and contact it concerns.
                These are defaulted from the parent request - remove them if they are not to be
                shared with the contractor.`;
        }
        return $localize`Confirm who owns this case, and which unit and contact it concerns. These are
            defaulted from the parent request.`;
    }

    get attachHelp(): string {
        return this.isWorkOrder
            ? $localize`Choose which of the files on the parent request the supplier should get. Copying a
                file puts it on the work order; you can also send it with the email.`
            : $localize`Choose which of the files on the parent request to copy onto the new case.`;
    }

    /** Nothing to choose from without a parent, so the step is not offered at all. */
    get hasFiles(): boolean {
        return this.files.length > 0;
    }

    get mailToSupplier(): boolean {
        return this.isWorkOrder && this.mailModeField.control?.value === CREATE_AND_SEND;
    }

    get supplierEmail(): string {
        return this.supplier?.email ?? '';
    }

    get finishLabel(): string {
        if (this.saving) {
            return $localize`Saving...`;
        }
        return this.mailToSupplier ? $localize`Create & Send Mail` : $localize`Create ${this.typeName}`;
    }

    get canFinish(): boolean {
        return !this.saving && this.summaryFS.getFormGroup().valid && this.peopleFS.getFormGroup().valid
            && this.followUpFS.getFormGroup().valid
            && (!this.isTemplated || this.templateFS.getFormGroup().valid)
            && (!this.isWorkOrder || this.workFS.getFormGroup().valid)
            && (!this.mailToSupplier || this.mailFS.getFormGroup().valid);
    }

    get supplierName(): string {
        return this.supplier?.name ?? $localize`the supplier`;
    }

    toggleShare(file: RequestFile) {
        file.share = !file.share;
    }

    toggleIncludeInMail(file: RequestFile) {
        file.includeInMail = !file.includeInMail;
    }

    finish() {
        this.saving = true;
        this.reqSvc.postNewRequest(this.buildRequest()).subscribe({
            next: created => this.created(created),
            error: failure => {
                this.saving = false;
                this.msgSvc.showError(failure, $localize`Creating ${this.typeName}`);
            },
        });
    }

    /**
     * The request is created whether or not its mail got out, so the dialog closes either way and
     * the caller slots the new request into the list. A mail that failed is the user's to retry from
     * the new request, so say so rather than leaving them to discover it.
     */
    private created(request: CreatedRequest) {
        this.dialogRef.close(request);

        if (request.mailError) {
            this.msgSvc.show(new MsgDef(
                $localize`Work order created, but the email to ${this.supplierName} was not sent: ${request.mailError}.
                    Open the new request to try sending it again.`, 'warn'
            ));
            return;
        }
        this.msgSvc.show(new MsgDef(request.mailSent
            ? $localize`Work order created and sent to ${this.supplierName}`
            : $localize`${this.typeName} created`));
    }

    private buildRequest() {
        return {
            ...this.summaryFS.getFormValue(),
            ...this.peopleFS.getFormValue(),
            ...this.followUpFS.getFormValue(),
            ...this.workValues(),
            parentId: this.parent?.id ?? null,
            // The plan came from a standard request, and for a recurring job this is the occurrence
            // it satisfies. No plannedDate: the date on the first step only dated the actions, and
            // the request's own planned date is being retired.
            templateId: this.template?.id ?? null,
            jobId: this.data.jobId ?? null,
            forumId: this.data.forumId ?? null,
            ballots: this.data.ballotId ? [{ ballotId: this.data.ballotId }] : [],
            // teamId names the OMC; the server derives the agency and the omcs row from it, always
            // through that team's primary agent - see RequestService::omcFor.
            teamId: this.context.teamId,
            srStatus: ServiceRequest.srOpen.id,
            copyAttachmentIds: this.files.filter(f => f.share).map(f => f.attachment.id),
            mailToSupplier: this.mailToSupplier,
            mailText: this.mailToSupplier ? this.mailTextField.control.value : null,
            mailAttachmentIds: this.mailToSupplier
                ? this.files.filter(f => f.includeInMail).map(f => f.attachment.id) : [],
        };
    }

    /** A case has no supplier, and with no supplier the server makes it a case - see ServiceRequest.requestTypes. */
    private workValues() {
        if (!this.isWorkOrder) {
            return {};
        }
        const work = this.workFS.getFormValue() as Partial<ServiceRequest>;
        return {
            ...work,
            // Lines default in from the supplier's history, so the ones left at zero are the ones
            // that did not apply this time - same as a purchase drops its unpriced items.
            requestBudget: (work.requestBudget ?? []).filter(line => Number(line.amount) !== 0),
        };
    }

    private loadOmcBudgetLists() {
        this.agentDataSvc.getOmcExpenseBCodes(this.context.teamId).subscribe(list => {
            this.bCodes.length = 0;
            this.bCodes.push(...list);
        });
        this.agentDataSvc.getOmcSchedules(this.context.teamId).subscribe(list => {
            this.schedules.length = 0;
            this.schedules.push(...list);
        });
    }

    /** Only a supplier with an email address can be mailed, so the choice follows the pick. */
    private supplierPicked(supplier: Supplier) {
        this.supplier = supplier;
        this.mailModeField.setPicklistItems(this.mailModeItems());
        // A supplier with an address is one you meant to send to - the common case leads.
        this.mailModeField.control.setValue(this.supplierEmail ? CREATE_AND_SEND : CREATE_ONLY);
        this.seedBudgetLines(supplier);
    }

    /** Sending is only on the menu once there is somewhere to send to, and the option says so. */
    private mailModeItems(): AbstractObject[] {
        if (this.supplier && !this.supplierEmail) {
            return [{ id: CREATE_ONLY, name: $localize`Create Only (no email address)` }];
        }
        const items: AbstractObject[] = [{ id: CREATE_ONLY, name: $localize`Create Only` }];
        if (this.supplierEmail) {
            items.push({ id: CREATE_AND_SEND, name: $localize`Create & Send Mail` });
        }
        return items;
    }

    /**
     * Open with the budget lines this OMC has already used for this supplier, so the common single
     * line case needs an amount and nothing else. They come from the single-record read (the picker
     * lists rows without them) with the OMC forced, because the server builds the list from the
     * purchase history of whichever team is asking. Changing supplier rebuilds them - the previous
     * supplier's codes mean nothing for the new one.
     */
    private seedBudgetLines(supplier: Supplier) {
        this.supplierSvc.getOne<Supplier>(supplier.id, null, this.context.teamId).subscribe(full => {
            this.budgetField.deleteAll();
            (full?.template ?? []).forEach(line => {
                const item = new RequestBudget();
                item.teamId = this.context.teamId;
                item.bCodeId = line.bCodeId;
                item.scheduleId = line.scheduleId ?? (this.schedules.length === 1 ? this.schedules[0].id : null);
                this.budgetField.control.addRow(item, false, false);
            });
            this.budgetField.control.refresh();
        });
    }

    private mailModeChanged() {
        if (!this.mailToSupplier) {
            this.files.forEach(f => f.includeInMail = false);
        }
    }
}
